import jwt from 'jwt-decode'

/*
===========================================
Tech4humans - Custom methods
-------------------------------------------
  Version: 1.0
===========================================
*/

/* ----------------------------------------
 Comparison
 ----------------------------------------*/
export const and = (a, b) => a && b
export const nand = (a, b) => !(a && b)
export const or = (a, b) => a || b
export const xor = (a, b) => or(a, b) && nand(a, b)

// Bitwise
export const andB = (a, b) => a & b
export const nandB = (a, b) => !(a & b)
export const orB = (a, b) => a | b
export const xorB = (a, b) => orB(a, b) && nandB(a, b)

/* ----------------------------------------
   Types
   ----------------------------------------*/
// not: null, undefined, NaN, empty string (""), false
export const isValid = (el) => el || el === 0
export const typeOf = (obj, type) => isValid(obj) && typeof obj === type
export const isArray = (array) => array && Array.isArray(array)
export const isFunction = (func) => typeOf(func, 'function')
export const isObject = (obj) => typeOf(obj, 'object') && !isArray(obj)
export const isNumber = (num) => typeOf(num, 'number')

/* ----------------------------------------
   Short version
   ----------------------------------------*/
export const objLength = (ob) => (isObject(ob) ? Object.keys(ob).length : 0)
export const getTimestamp = (date) => new Date(date).getTime()

/* ----------------------------------------
   Array methods
   ----------------------------------------*/
export const find = (array, condition, isReverse) => {
  if (isArray(array) && isFunction(condition)) {
    let lastIndex = array.length - 1

    if (!isReverse) {
      for (let index = 0; index <= lastIndex; index++)
        if (condition(array[index])) return array[index]
    } else {
      for (let index = lastIndex; index >= 0; index--)
        if (condition(array[index])) return array[index]
    }
  }
  return null
}

export const findBetween = (array, start, end, condition, isReverse) => {
  if (isArray(array) && isNumber(start) && isNumber(end)) {
    const list = array.slice(start, end)
    return find(list, condition, isReverse)
  }
  return null
}

export const arrayToObj = (array, key) => {
  if (isArray(array)) {
    if (!key) {
      return { ...array }
    } else {
      return array.reduce((obj, cur) => {
        obj[cur?.[key]] = cur
        return obj
      }, {})
    }
  } else {
    return {}
  }
}

/* ----------------------------------------
   Auth
   ----------------------------------------*/
export const isValidToken = (token) => {
  if (token) {
    const expireDate = new Date(jwt(token)?.exp * 1000).getTime()
    const now = new Date().getTime()

    if (now < expireDate) return true
  }
  return false
}

export const convertSlaTime = (
  _time,
  { fixingSeconds, withSummary } = {
    fixingSeconds: false,
    withSummary: false,
  }
) => {
  if (isNumber(_time)) {
    let timeFormat = _time >= 0 ? '' : '-'
    let timeFormatSummary = _time >= 0 ? '' : '-'
    const time = Math.abs(_time)
    const days = Math.floor(time / 86400)
    const hours = Math.floor((time % 86400) / 3600)
    const minutes = Math.floor((time % 3600) / 60)
    const seconds = Math.floor(time % 60)

    if (days > 0) {
      timeFormat += `${days < 10 ? '0' : ''}${days}d `
    }

    if (hours > 0) {
      timeFormat += `${hours < 10 ? '0' : ''}${hours}h `
    }

    if (minutes > 0) {
      timeFormat += `${minutes < 10 ? '0' : ''}${minutes}m `
    }

    if (seconds > 0) {
      timeFormat += `${seconds < 10 ? '0' : ''}${seconds}s`
    } else if (fixingSeconds) {
      timeFormat += '00s'
    }

    if (withSummary) {
      if (days > 0) {
        timeFormatSummary += `${days < 10 ? '0' : ''}${days}d `
      }
      if (hours > 0) {
        timeFormatSummary += `${hours < 10 ? '0' : ''}${hours}h `
      }
      if (minutes > 0 && (hours === 0 || days === 0)) {
        timeFormatSummary += `${minutes < 10 ? '0' : ''}${minutes}m `
      }

      if (seconds > 0) {
        if (seconds > 0 && (hours === 0 || days === 0) && minutes === 0) {
          timeFormatSummary += `${seconds < 10 ? '0' : ''}${seconds}s`
        } else if (days === 0 && hours === 0) {
          timeFormatSummary += `${seconds < 10 ? '0' : ''}${seconds}s`
        }
      }

      return {
        full: timeFormat,
        summary: timeFormatSummary,
      }
    }

    return timeFormat
  }

  return _time
}

export const getAvailability = (availabilities, avail_id) => {
  if (!isArray(availabilities)) {
    return null
  }

  const types = {
    away: 'Ausente',
    backsoon: 'Volto logo',
    online: 'Online',
  }
  let availability = availabilities.find((a) => a.id === avail_id)

  if (!availability) {
    availability = availabilities
      .map((a) => a.children)
      .flat()
      .find((a) => a?.id === avail_id)
  }

  if (!availability) return null

  return { ...availability, typeInfo: types[availability.type] }
}

/**
 * It replaces all special characters with their non-special counterparts
 * @param [text] - The text to be processed.
 * @returns the text with all the special characters removed.
 */
export const removeSpecials = (text = '') => {
  return text.normalize("NFD").replace(/[\u0300-\u036f]/g, "") 
}
